import React, { useState, useEffect, useCallback } from 'react';
import { Lock, PenLine, Pin, Brain, History, Eye, Save, RotateCcw, ChevronDown, ChevronUp, X } from 'lucide-react';
import { withAuthHeaders } from '../utils/auth';

const API = process.env.REACT_APP_BACKEND_URL;

const pct = v => (v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`);
const formatDate = d => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

function Section({ icon: Icon, title, hint, right, children, collapsible = false, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card">
      <div className="card-header" style={{ justifyContent: 'space-between', cursor: collapsible ? 'pointer' : 'default' }}
           onClick={() => collapsible && setOpen(!open)}>
        <span className="card-title"><Icon size={16} style={{ marginRight: 6, verticalAlign: -3 }} />{title}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {right}
          {collapsible && (open ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
        </span>
      </div>
      {hint && <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginBottom: open ? 10 : 0 }}>{hint}</div>}
      {open && children}
    </div>
  );
}

function PreviewModal({ text, title, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 760, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span className="modal-title" style={{ marginBottom: 0 }}>{title}</span>
          <button className="btn btn-icon btn-secondary" onClick={onClose}><X size={16} /></button>
        </div>
        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem', fontFamily: 'var(--font-mono)', maxHeight: '70vh', overflowY: 'auto', background: 'var(--bg-input)', padding: 12, borderRadius: 'var(--radius-md)', margin: 0 }}>
          {text}
        </pre>
      </div>
    </div>
  );
}

export default function PromptPage({ auth, onOpen }) {
  const [data, setData] = useState(null);
  const [versions, setVersions] = useState([]);
  const [style, setStyle] = useState('');
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);

  const api = useCallback(async (path, opts = {}) => {
    const r = await fetch(`${API}/api/prompt${path}`, { ...opts, headers: withAuthHeaders(auth, { 'Content-Type': 'application/json' }) });
    return r.json();
  }, [auth]);

  const load = useCallback(async () => {
    const [p, v] = await Promise.all([api(''), api('/versions')]);
    setData(p);
    setStyle(p.style || '');
    setVersions(v.versions || []);
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const saveStyle = async () => {
    setSaving(true); setMsg('');
    const r = await api('/style', { method: 'PUT', body: JSON.stringify({ style, note }) });
    setMsg(r.ok ? `Сохранено — версия промпта ${r.version}` : (r.detail || 'Ошибка'));
    setSaving(false); setNote('');
    if (r.ok) load();
  };

  const preview = async () => {
    const r = await api('/preview');
    setModal({ title: `Итоговый промпт — версия ${r.version} (${r.chars} символов)`, text: r.prompt });
  };

  const showVersion = async v => {
    const r = await api(`/versions/${v}`);
    const facts = (r.facts || []).map(f => `### ${f.title}\n${f.content}`).join('\n\n');
    setModal({ title: `Версия ${v}: ${r.reason || ''}`, text: `СТИЛЬ:\n${r.style}\n\nФАКТЫ (${r.facts?.length || 0}):\n${facts}\n\nВЫУЧЕННЫХ ПРАВИЛ: ${r.rule_ids?.length || 0}` });
  };

  const doRollback = async v => {
    if (!window.confirm(`Откатить стиль, факты и выученные правила к версии ${v}? Будет создана новая версия.`)) return;
    const r = await api(`/versions/${v}/rollback`, { method: 'POST' });
    setMsg(r.ok ? `Откат выполнен — новая версия ${r.version}` : (r.error || 'Ошибка'));
    load();
  };

  if (!data) return <div className="empty-state"><div className="loading-spinner" /></div>;
  const styleChanged = style !== (data.style || '');
  const factsChars = data.facts.reduce((s, f) => s + f.content.length, 0);

  return (
    <div data-testid="prompt-page">
      <div className="card">
        <div className="card-header" style={{ justifyContent: 'space-between' }}>
          <span className="card-title">Системный промпт AI</span>
          <span className="badge badge-info">версия {data.version || '—'} · {data.total_chars} симв.</span>
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
          Промпт собирается из блоков сверху вниз. Ядро задаёт неизменные правила, остальное настраивается.
          Каждое изменение — новая версия: ниже видно, как менялись доля решённых AI тикетов и оценки.
        </div>
        <button className="btn btn-secondary btn-sm" style={{ marginTop: 10 }} onClick={preview}><Eye size={14} /> Итоговый промпт целиком</button>
        {!data.style_migrated && (
          <div className="alert alert-error" style={{ marginTop: 10 }}>
            Сейчас как «Стиль» целиком используется старый промпт из настроек — он может дублировать ядро и факты. Перенесите факты о сервисе в закреплённые 📌 статьи базы знаний, а в стиле оставьте только тон и манеру общения (или нажмите «Стиль по умолчанию») и сохраните.
          </div>
        )}
      </div>

      {msg && <div className="alert alert-info">{msg}</div>}

      <Section icon={Lock} title="1. Ядро" collapsible defaultOpen={false}
               hint="Безопасность, что AI делает сам и когда зовёт менеджера, формат, краткость. Задаётся в коде и не меняется ни отсюда, ни самообучением — при конфликте приоритет всегда у ядра.">
        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.74rem', fontFamily: 'var(--font-mono)', background: 'var(--bg-input)', padding: 10, borderRadius: 'var(--radius-md)', maxHeight: 360, overflowY: 'auto', margin: 0 }}>
          {data.core}
        </pre>
      </Section>

      <Section icon={PenLine} title="2. Стиль и поведение" right={<span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{style.length} симв.</span>}
               hint="Тон, язык, манера общения. Факты о сервисе сюда не пишите — для них закреплённые статьи базы знаний. Переменные: {service_name}, {main_bot}.">
        <textarea className="input" rows={7} value={style} onChange={e => setStyle(e.target.value)} style={{ resize: 'vertical' }} data-testid="prompt-style" />
        <input className="input" style={{ marginTop: 8 }} placeholder="Что изменили (попадёт в историю версий)" value={note} onChange={e => setNote(e.target.value)} />
        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" onClick={saveStyle} disabled={saving || !styleChanged}><Save size={14} /> {saving ? 'Сохранение…' : 'Сохранить стиль'}</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setStyle(data.default_style)}>Стиль по умолчанию</button>
          {styleChanged && <button className="btn btn-secondary btn-sm" onClick={() => setStyle(data.style)}>Отменить правки</button>}
        </div>
      </Section>

      <Section icon={Pin} title={`3. Факты о сервисе (${data.facts.length})`} right={<span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{factsChars} симв.</span>}
               hint="Закреплённые 📌 статьи базы знаний — всегда целиком в промпте. Закрепляйте и редактируйте их во вкладке «База». Самообучение может предлагать их правку — она применяется только после вашего одобрения.">
        {data.facts.length === 0 ? <div className="empty-text">Нет закреплённых статей.</div> :
          data.facts.map(f => (
            <div key={f.id} className="data-row">
              <span className="data-label">📌 {f.title}</span>
              <span className="data-value" style={{ color: 'var(--text-muted)' }}>{f.content.length} симв.</span>
            </div>
          ))}
        <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={() => onOpen('knowledge')}>Открыть базу знаний</button>
      </Section>

      <Section icon={Brain} title={`4. Выученные правила (${data.rules.length})`}
               hint="Одобренные уроки самообучения. Предлагаются AI по итогам разбора тикетов или созданы вами из исправлений ответов.">
        {data.rules.length === 0 ? <div className="empty-text">Пока нет одобренных правил.</div> :
          data.rules.map(r => (
            <div key={r.id} style={{ fontSize: '0.82rem', padding: '6px 0', borderBottom: '1px solid var(--border-default)' }}>
              <b>{r.title}</b>{r.auto_applied && <span className="badge badge-warning" style={{ marginLeft: 6 }}>авто</span>}
              <div style={{ color: 'var(--text-secondary)' }}>{r.content}</div>
            </div>
          ))}
        <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={() => onOpen('learning')}>Управлять в «Обучении»</button>
      </Section>

      <Section icon={History} title="Версии и эффект" collapsible
               hint="Статистика по тикетам, где первый ответ AI был дан на этой версии. Чтобы сравнение было честным, дайте версии поработать хотя бы несколько десятков тикетов.">
        {versions.length === 0 ? <div className="empty-text">Версий ещё нет.</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.76rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: 'var(--text-secondary)', textAlign: 'left' }}>
                  <th style={{ padding: 4 }}>Версия</th><th>Что изменилось</th><th>Тикетов</th><th>Решено AI</th><th>Качество</th><th>👍/👎</th><th />
                </tr>
              </thead>
              <tbody>
                {versions.map(v => (
                  <tr key={v.version} style={{ borderTop: '1px solid var(--border-default)' }}>
                    <td style={{ padding: 4, whiteSpace: 'nowrap' }}>
                      <b>v{v.version}</b>{v.version === data.version && <span className="badge badge-success" style={{ marginLeft: 4 }}>текущая</span>}
                      <div style={{ color: 'var(--text-muted)' }}>{formatDate(v.created_at)}</div>
                    </td>
                    <td>{v.reason}</td>
                    <td>{v.tickets}</td>
                    <td>{pct(v.ai_resolution_rate)}</td>
                    <td>{v.avg_ai_quality ?? '—'}</td>
                    <td>{v.ratings.up}/{v.ratings.down}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-icon btn-secondary btn-sm" title="Посмотреть" onClick={() => showVersion(v.version)}><Eye size={12} /></button>
                      {v.version !== data.version && (
                        <button className="btn btn-icon btn-secondary btn-sm" title="Откатить к этой версии" onClick={() => doRollback(v.version)} style={{ marginLeft: 4 }}><RotateCcw size={12} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {modal && <PreviewModal title={modal.title} text={modal.text} onClose={() => setModal(null)} />}
    </div>
  );
}
